import React, { useState, useMemo, useEffect } from 'react'
import { ScatterChart, Scatter, XAxis, YAxis, ResponsiveContainer, ReferenceLine } from 'recharts'
import { MarketViewSearchResult } from '../../services/api'
import { ChevronDown } from 'lucide-react'
import TradeoffTooltip from './TradeoffTooltip'
import { describeScore } from '../../lib/scoreDisplay'

// Inline chart (shared with and without zoom wrapper)
const ChartContent: React.FC<{
  scatterData: Array<{
    x: number;
    y: number;
    product: { name: string };
    vendor: { name: string };
    result: MarketViewSearchResult;
  }>;
  xDomain: number[];
  yDomain: number[];
  CustomDot: React.ComponentType<any>;
}> = ({ scatterData, xDomain, yDomain, CustomDot }) => (
  <ResponsiveContainer width="100%" height="100%" debounce={1}>
    <ScatterChart margin={{ top: 36, right: 32, bottom: 36, left: 32 }}>
      {/* Vertical lines */}
      <ReferenceLine
        x={xDomain[0]}
        stroke="#444444"
        strokeWidth={2}
      />
      <ReferenceLine
        x={xDomain[1]}
        stroke="#444444"
        strokeWidth={2}
      />
      {/* Horizontal lines */}  
      <ReferenceLine
        y={yDomain[0]}
        stroke="#444444"
        strokeWidth={2}
      />
      <ReferenceLine
        y={yDomain[1]}
        stroke="#444444"
        strokeWidth={2}
      />
      {/* Crosshair lines */}
      <ReferenceLine
        x={(xDomain[0] + xDomain[1]) / 2}
        stroke="#444444"
        strokeWidth={2}
      />
      <ReferenceLine
        y={(yDomain[0] + yDomain[1]) / 2}
        stroke="#444444"
        strokeWidth={2}
      />
      <XAxis type="number" dataKey="x" domain={xDomain} hide={true} />
      <YAxis type="number" dataKey="y" domain={yDomain} hide={true} />
      <Scatter data={scatterData} shape={<CustomDot />} />
    </ScatterChart>
  </ResponsiveContainer>
);

// Map dimension keys to display labels
const dimensionOptions = [
  { key: 'need_fit', label: 'Need Fit' },
  { key: 'technical', label: 'Technical' },
  { key: 'manufacturing', label: 'Production Readiness' },
  { key: 'vc_metrics', label: 'Company Stability' },
  { key: 'dod_partnership', label: 'DoW Partnership' },
] as const

interface TradeoffsViewProps {
  results: MarketViewSearchResult[]
}

const TradeoffsView: React.FC<TradeoffsViewProps> = ({ results }) => {
  const [xAxis, setXAxis] = useState<string>('need_fit')
  const [yAxis, setYAxis] = useState<string>('technical')
  const [hoveredProduct, setHoveredProduct] = useState<MarketViewSearchResult | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [ZoomWrapper, setZoomWrapper] = useState<{
    TransformWrapper: React.ComponentType<any>
    TransformComponent: React.ComponentType<any>
  } | null>(null)
  const transformRef = React.useRef<any>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [zoomScale, setZoomScale] = useState(1)

  useEffect(() => {
    import('react-zoom-pan-pinch')
      .then((m) => setZoomWrapper({ TransformWrapper: m.TransformWrapper, TransformComponent: m.TransformComponent }))
      .catch(() => { /* chart renders without zoom when package unavailable (e.g. Docker) */ })
  }, [])

  // Subscribe to zoom scale so dot label font size can scale with zoom (text stays readable at any zoom level)
  useEffect(() => {
    if (!ZoomWrapper) return
    let unsub: (() => void) | undefined
    const id = setTimeout(() => {
      if (!transformRef.current) return
      setZoomScale(transformRef.current.state?.scale ?? 1)
      unsub = transformRef.current.instance?.onChange?.((ctx: { state: { scale: number } }) => {
        setZoomScale(ctx.state.scale)
      })
    }, 0)
    return () => {
      clearTimeout(id)
      unsub?.()
    }
  }, [ZoomWrapper])

  // Transform results into scatter data with jitter for better visibility
  const { scatterData, xDomain, yDomain, excludedCount, excludedAxes } = useMemo(() => {
    // Add larger random jitter to distribute points better
    const addJitter = (value: number) => {
      const jitter = (Math.random() - 0.5) * 20 // +/- 10 points
      return Math.max(0, Math.min(100, value + jitter))
    }

    // A dimension the backend never measured has no coordinate. Plotting its
    // placeholder would put the product somewhere it was never assessed, so it
    // is left off the chart and counted below it instead (DEV-584).
    const coordinate = (result: MarketViewSearchResult, axis: string): number | null => {
      const raw = result.scores[axis as keyof typeof result.scores]
      if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
      // `overall_status` is a state word, not a dimension - nothing to plot.
      if (typeof raw === 'string') return null
      return describeScore(raw).value
    }

    const unplottableAxes = new Set<string>()
    const axisLabel = (key: string) =>
      dimensionOptions.find(d => d.key === key)?.label ?? key

    const data = results
      .map(result => {
        const x = coordinate(result, xAxis)
        const y = coordinate(result, yAxis)
        if (x === null || y === null) {
          if (x === null) unplottableAxes.add(axisLabel(xAxis))
          if (y === null) unplottableAxes.add(axisLabel(yAxis))
          return null
        }

        return {
          x: addJitter(x),
          y: addJitter(y),
          product: result.product,
          vendor: result.vendor,
          scores: result.scores,
          result: result,
        }
      })
      .filter((d): d is NonNullable<typeof d> => d !== null)

    if (data.length === 0) {
      return {
        scatterData: data,
        xDomain: [0, 100],
        yDomain: [0, 100],
        excludedCount: results.length,
        excludedAxes: [...unplottableAxes],
      }
    }

    // Calculate dynamic domains with 10% margin
    const xValues = data.map(d => d.x)
    const yValues = data.map(d => d.y)

    const xMin = Math.min(...xValues)
    const xMax = Math.max(...xValues)
    const yMin = Math.min(...yValues)
    const yMax = Math.max(...yValues)

    const xRange = xMax - xMin
    const yRange = yMax - yMin

    const xMargin = xRange * 0.1
    const yMargin = yRange * 0.1

    return {
      scatterData: data,
      xDomain: [Math.max(0, xMin - xMargin), Math.min(100, xMax + xMargin)],
      yDomain: [Math.max(0, yMin - yMargin), Math.min(100, yMax + yMargin)],
      excludedCount: results.length - data.length,
      excludedAxes: [...unplottableAxes],
    }
  }, [results, xAxis, yAxis])

  // Counter-scale dot labels so text stays roughly the same visual size when zooming (SVG units shrink when zoomed in so the library’s scale brings them back)
  const createCustomDot = (scale: number) => (props: any) => {
    const { cx, cy, payload } = props
    const safeScale = Math.max(0.4, scale)
    const productFontSize = Math.max(4, Math.min(24, Math.round(10 / safeScale)))
    const vendorFontSize = Math.max(3, Math.min(20, Math.round(8 / safeScale)))
    return (
      <g>
        <circle 
          cx={cx} 
          cy={cy} 
          r={6} 
          fill="var(--primary)"
          stroke="var(--foreground)"
          strokeWidth={2}
          className="cursor-pointer hover:r-8 transition-all"
          onMouseEnter={() => {
            setHoveredProduct(payload.result)
            setTooltipPosition({ x: cx, y: cy })
          }}
          onMouseLeave={() => setHoveredProduct(null)}
        />
        <text
          x={cx}
          y={cy - 12}
          textAnchor="middle"
          fontSize={productFontSize}
          className="fill-foreground pointer-events-none"
        >
          {payload.product.name}
        </text>
        <text
          x={cx}
          y={cy - 2}
          textAnchor="middle"
          fontSize={vendorFontSize}
          className="fill-muted-foreground pointer-events-none"
        >
          {payload.vendor.name}
        </text>
      </g>
    )
  }

  return (
    <div ref={containerRef} className="absolute inset-0 flex flex-col min-h-0">
      {/* Mobile: axis selectors in a top bar */}
      <div className="flex sm:hidden flex-wrap items-center justify-center gap-2 p-2 border-b border-border bg-background/80 z-10 flex-shrink-0">
        <AxisSelector
          axisLabel="X"
          value={xAxis}
          onChange={setXAxis}
          options={dimensionOptions}
          position="top"
        />
        <AxisSelector
          axisLabel="Y"
          value={yAxis}
          onChange={setYAxis}
          options={dimensionOptions}
          position="top"
        />
      </div>

      {/* Desktop: floating axis selectors */}
      <div className="hidden sm:block absolute left-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
        <div className="pointer-events-auto">
          <AxisSelector
            axisLabel="Y"
            value={yAxis}
            onChange={setYAxis}
            options={dimensionOptions}
            position="left"
          />
        </div>
      </div>
      <div className="hidden sm:block absolute top-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
        <div className="pointer-events-auto">
          <AxisSelector
            axisLabel="X"
            value={xAxis}
            onChange={setXAxis}
            options={dimensionOptions}
            position="top"
          />
        </div>
      </div>

      {/* Axis Labels - hidden on small screens to avoid overlap */}
      <div className="hidden sm:block absolute bottom-20 left-20 text-xs text-muted-foreground">
        Low {dimensionOptions.find(d => d.key === xAxis)?.label}
      </div>
      <div className="hidden sm:block absolute bottom-20 right-16 text-xs text-muted-foreground">
        High {dimensionOptions.find(d => d.key === xAxis)?.label}
      </div>
      <div className="hidden sm:block absolute top-32 right-4 text-xs text-muted-foreground rotate-90 origin-right">
        Low {dimensionOptions.find(d => d.key === yAxis)?.label}
      </div>
      <div className="hidden sm:block absolute bottom-32 right-4 text-xs text-muted-foreground rotate-90 origin-right">
        High {dimensionOptions.find(d => d.key === yAxis)?.label}
      </div>

      {/* Chart — fixed-size viewport */}
      <div className="flex-1 min-h-0 w-full overflow-hidden flex items-center justify-center p-4 sm:pl-40 sm:pt-14 sm:pr-8 sm:pb-6" onMouseLeave={() => setHoveredProduct(null)}>
        <div className="w-full h-full overflow-hidden sm:min-h-[440px] sm:max-h-[76vh] sm:rounded-md sm:border sm:border-border sm:bg-card/30">
          {ZoomWrapper ? (
            (() => {
              const { TransformWrapper, TransformComponent } = ZoomWrapper
              return (
                <TransformWrapper
                  ref={transformRef}
                  initialScale={1}
                  minScale={0.4}
                  maxScale={4}
                  centerOnInit
                  wheel={{ step: 0.3 }}
                  doubleClick={{ mode: 'reset' }}
                  panning={{ velocityDisabled: true }}
                >
                  <TransformComponent
                    wrapperStyle={{ width: '100%', height: '100%' }}
                    contentStyle={{ width: '100%', height: '100%' }}
                  >
                    <ChartContent scatterData={scatterData} xDomain={xDomain} yDomain={yDomain} CustomDot={createCustomDot(zoomScale)} />
                  </TransformComponent>
                </TransformWrapper>
              )
            })()
          ) : (
            <ChartContent scatterData={scatterData} xDomain={xDomain} yDomain={yDomain} CustomDot={createCustomDot(1)} />
          )}
        </div>
      </div>

      {/* Products with no coordinate on one of the chosen axes */}
      {excludedCount > 0 && (
        <div
          data-testid="tradeoff-excluded"
          className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[11px] text-muted-foreground text-center px-3"
        >
          {excludedCount} of {results.length} products not plotted - {excludedAxes.join(' and ')} not scored
        </div>
      )}

      {/* Hover Tooltip */}
      {hoveredProduct && (
        <TradeoffTooltip
          result={hoveredProduct}
          position={tooltipPosition}
        />
      )}
    </div>
  )
}

// Axis selector dropdown
const AxisSelector: React.FC<{
  axisLabel: string
  value: string
  onChange: (value: string) => void
  options: typeof dimensionOptions
  position?: 'left' | 'top'
}> = ({ axisLabel, value, onChange, options, position = 'top' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectedLabel = options.find(o => o.key === value)?.label

  return (
    <div className="relative flex items-center gap-2">
      <span className="text-muted-foreground font-semibold text-sm shrink-0 sm:hidden">{axisLabel}:</span>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-md text-sm hover:bg-muted shadow-sm font-medium"
        title={`Select ${axisLabel}-axis dimension`}
      >
        {selectedLabel}
        <ChevronDown className="w-4 h-4" />
      </button>
      {isOpen && (
        <div className={`absolute ${position === 'left' ? 'left-0 top-full' : 'top-full left-0'} mt-1 bg-popover border border-border rounded-md shadow-md z-20 min-w-full overflow-hidden`}>
          {options.map(option => (
            <button
              key={option.key}
              onClick={() => {
                onChange(option.key)
                setIsOpen(false)
              }}
              className="block w-full text-left px-4 py-2 text-sm hover:bg-accent whitespace-nowrap"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default TradeoffsView
